import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQs.css';

const faqData = [
  {
    category: 'Orders & Customisation',
    icon: '🎁',
    items: [
      {
        q: 'Are you accepting bulk orders?',
        a: 'Yes, we accept bulk orders! Please get in touch with us on WhatsApp at +91 63555 92849 for more information on trade discounts and bulk pricing.',
      },
      {
        q: 'What is your cancellation policy?',
        a: 'Since all our products are personalised and made to your specific requirements, we do not accept cancellations or changes once the order is placed. Please double-check all personalisation details before confirming your order.',
      },
      {
        q: 'Do you have Cash on Delivery (COD)?',
        a: 'We have stopped accepting Cash on Delivery. Please use our other available modes of payment — including UPI, credit/debit cards, and net banking — to place your order.',
      },
    ],
  },
  {
    category: 'Shipping & Tracking',
    icon: '🚚',
    items: [
      {
        q: 'How can I track my order?',
        a: 'We currently send order and tracking details via WhatsApp. If you would like to know your tracking number or order status, please send us a message on WhatsApp at +91 63555 92849.\n\nWe are working on an improved tracking process — once your order is ready to ship, we will send the shipping details and tracking link to your registered email address as well.',
      },
      {
        q: 'What is your delivery timeline?',
        a: 'Our standard delivery timeline is 6–8 working days across India, including both production/making time and shipping time.\n\nHowever, some products may require additional processing time or may take longer with the shipping partner — up to 10–12 working days in such cases.\n\nPlease refer to the individual product page for specific delivery timelines, or WhatsApp us at +91 63555 92849 for any queries.',
      },
      {
        q: 'Are there any additional charges on shipping?',
        a: 'We provide free shipping across India on most of our products. Please check the individual product page for details on shipping charges for your specific order.',
      },
      {
        q: 'What are the shipping rates and processing times?',
        a: 'Our delivery services are currently available only to locations within continental India.\n\nPlease allow 1–3 business days (Monday–Saturday) of processing time before dispatch.\n\nShipping: Economy India delivery takes 5–7 business days (Mon–Sat) at a standard rate of ₹80 per KG.',
      },
      {
        q: 'What if I haven\'t received my order?',
        a: 'If you haven\'t received your order within 10 days of your shipping confirmation, please contact us at fcreation08@gmail.com or call/WhatsApp +91 6355 592849 with your name and order number, and we will investigate immediately.',
      },
      {
        q: 'Can I change my delivery address after placing an order?',
        a: 'We cannot change the delivery address once the order is in transit. If you need to update your delivery address, please contact us within 12 hours of placing your order at +91 6355 592849.',
      },
    ],
  },
  {
    category: 'Returns & Exchange',
    icon: '↩️',
    items: [
      {
        q: 'Do you accept returns or exchanges?',
        a: 'Since your product is customised specifically to your requirements, we do not accept returns or exchanges.\n\nHowever, a product is eligible for return or replacement under the following conditions:\n1. A wrong product has been dispatched that does not match the item selected during order confirmation.\n2. You identify a quality or manufacturing defect on the received product.\n3. The product is received in a damaged condition.\n\nIf your order qualifies, we offer either a full refund or re-fulfilment of your order.',
      },
      {
        q: 'What is required to raise a return or damage claim?',
        a: 'A product opening video is mandatory for any claim — the video must be uncut and without any pause from the moment you begin opening the package. Without this video, we will be unable to process your claim. Please record this video every time you open a package from us.',
      },
      {
        q: 'What is the refund policy?',
        a: 'Due to the personalised nature of our customised products, all sales are final and we cannot offer refunds or exchanges unless the product qualifies under our return conditions (wrong item, manufacturing defect, or damaged on arrival).\n\nIf your return is approved, a full refund or re-fulfilment will be arranged.',
      },
    ],
  },
  {
    category: 'Payments',
    icon: '💳',
    items: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major payment modes including UPI (GPay, PhonePe, Paytm), credit cards, debit cards, and net banking. Cash on Delivery (COD) is no longer available.',
      },
      {
        q: 'Is my payment information secure?',
        a: 'Yes, absolutely. We use Razorpay for processing all payments. Your card data is never stored on our servers. All payment information is encrypted through the Payment Card Industry Data Security Standard (PCI-DSS) and is used only for the duration needed to complete your transaction.',
      },
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span className={`faq-icon ${open ? 'faq-icon--open' : ''}`}>
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="faq-answer">
          {a.split('\n').map((line, i) =>
            line.trim() ? (
              <p key={i} style={{ marginBottom: line.startsWith('1)') || line.startsWith('2)') || line.startsWith('3)') ? 6 : 10 }}>
                {line}
              </p>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

const FAQs = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = faqData
    .map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          (!search ||
            item.q.toLowerCase().includes(search.toLowerCase()) ||
            item.a.toLowerCase().includes(search.toLowerCase())) &&
          (!activeCategory || cat.category === activeCategory)
      ),
    }))
    .filter((cat) => cat.items.length > 0);

  return (
    <div className="faqs-page">
      {/* Hero */}
      <section className="inner-hero">
        <div className="inner-hero__content container">
          <span className="about-eyebrow">Support Centre</span>
          <h1 className="inner-hero__title">Frequently Asked<br />Questions</h1>
          <p className="inner-hero__sub">
            Everything you need to know about ordering, shipping, personalisation, and returns.
          </p>

          {/* Search */}
          <div className="faq-search-wrap">
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              className="faq-search"
              type="text"
              placeholder="Search your question…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="faq-search-clear" onClick={() => setSearch('')}>✕</button>
            )}
          </div>
        </div>
      </section>

      <div className="faqs-body container">
        {/* Category pills */}
        <div className="faq-cats">
          <button
            className={`faq-cat-pill ${!activeCategory ? 'active' : ''}`}
            onClick={() => setActiveCategory(null)}
          >
            All Topics
          </button>
          {faqData.map((cat) => (
            <button
              key={cat.category}
              className={`faq-cat-pill ${activeCategory === cat.category ? 'active' : ''}`}
              onClick={() => setActiveCategory(activeCategory === cat.category ? null : cat.category)}
            >
              {cat.icon} {cat.category}
            </button>
          ))}
        </div>

        {/* FAQ sections */}
        {filtered.length === 0 ? (
          <div className="faq-empty">
            <span>🔍</span>
            <p>No results found for "<strong>{search}</strong>"</p>
            <button className="btn btn-outline btn-sm" onClick={() => setSearch('')}>
              Clear Search
            </button>
          </div>
        ) : (
          <div className="faq-sections">
            {filtered.map((cat) => (
              <div key={cat.category} className="faq-section">
                <div className="faq-section__header">
                  <span className="faq-section__icon">{cat.icon}</span>
                  <h2 className="faq-section__title">{cat.category}</h2>
                </div>
                <div className="faq-list">
                  {cat.items.map((item, i) => (
                    <FAQItem key={i} q={item.q} a={item.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Still have questions */}
        <div className="faq-contact-card">
          <span className="faq-contact-card__icon">💬</span>
          <div>
            <h3>Still have questions?</h3>
            <p>
              Our team is happy to help. Reach out via WhatsApp or email and we'll get back to you quickly.
            </p>
          </div>
          <div className="faq-contact-card__actions">
            <Link to="/contact" className="btn btn-primary-warm">Contact Us</Link>
            <a
              href="https://wa.me/916355592849"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
            >
              💬 WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
